export const BILLIONAIRE_MASTER_PRIZES = [
  1_000, 2_000, 3_000, 5_000, 7_500, 10_000, 15_000, 25_000, 40_000, 60_000,
  100_000, 150_000, 200_000, 300_000, 400_000, 500_000, 600_000, 750_000, 900_000, 1_000_000,
] as const;

export const BILLIONAIRE_BONUS_EFFECTS = [
  'cash-half-next', 'cash-next', 'lose-quarter', 'next-x2', 'next-x3',
] as const;

export type BillionaireBonusEffect = (typeof BILLIONAIRE_BONUS_EFFECTS)[number];
export type BillionaireMultiplier = 1 | 2 | 3;

export type BillionaireEconomyState = {
  bank: number;
  pendingMultiplier: BillionaireMultiplier;
  correct: number;
  wrong: number;
  answered: number;
};

export function buildPrizeLadder(questionCount: number): number[] {
  if (!Number.isInteger(questionCount) || questionCount < 5 || questionCount > 20) {
    throw new RangeError('Kim Milyarder Olmak İster? 5–20 soru içermelidir.');
  }
  if (questionCount === BILLIONAIRE_MASTER_PRIZES.length) return [...BILLIONAIRE_MASTER_PRIZES];
  const lastIndex = BILLIONAIRE_MASTER_PRIZES.length - 1;
  return Array.from({ length: questionCount }, (_, index) => {
    const sourceIndex = Math.round((index * lastIndex) / (questionCount - 1));
    return BILLIONAIRE_MASTER_PRIZES[sourceIndex];
  });
}

export function buildBonusSchedule(questionCount: number): number[] {
  if (!Number.isInteger(questionCount) || questionCount < 5 || questionCount > 20) {
    throw new RangeError('Bonus takvimi 5–20 soru için hesaplanabilir.');
  }
  const roundCount = questionCount <= 8 ? 1 : questionCount <= 14 ? 2 : 3;
  const positions: number[] = [];
  for (let round = 1; round <= roundCount; round += 1) {
    let position = Math.round((round * questionCount) / (roundCount + 1));
    position = Math.max((positions.at(-1) ?? 0) + 1, Math.min(questionCount - 1, position));
    positions.push(position);
  }
  return positions;
}

function hashSeed(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

export function createBonusDeck(seedKey: string, round: number): BillionaireBonusEffect[] {
  const deck = [...BILLIONAIRE_BONUS_EFFECTS];
  const random = seededRandom(hashSeed(`${seedKey}:${round}`));
  for (let index = deck.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    [deck[index], deck[target]] = [deck[target], deck[index]];
  }
  return deck;
}

export function initialEconomyState(): BillionaireEconomyState {
  return { bank: 0, pendingMultiplier: 1, correct: 0, wrong: 0, answered: 0 };
}

export function applyQuestionAnswer(
  state: BillionaireEconomyState,
  basePrize: number,
  correct: boolean,
): { state: BillionaireEconomyState; appliedMultiplier: BillionaireMultiplier; awardedAmount: number } {
  const appliedMultiplier = correct ? state.pendingMultiplier : 1;
  const awardedAmount = correct ? basePrize * appliedMultiplier : 0;
  return {
    state: {
      bank: state.bank + awardedAmount,
      pendingMultiplier: correct ? 1 : state.pendingMultiplier,
      correct: state.correct + (correct ? 1 : 0),
      wrong: state.wrong + (correct ? 0 : 1),
      answered: state.answered + 1,
    },
    appliedMultiplier,
    awardedAmount,
  };
}

export function applyBonusEffect(
  state: BillionaireEconomyState,
  effect: BillionaireBonusEffect,
  nextBasePrize: number,
): { state: BillionaireEconomyState; amountDelta: number } {
  let amountDelta = 0;
  let pendingMultiplier = state.pendingMultiplier;
  if (effect === 'cash-half-next') amountDelta = Math.round(nextBasePrize * 0.5);
  if (effect === 'cash-next') amountDelta = nextBasePrize;
  if (effect === 'lose-quarter') amountDelta = -Math.min(state.bank, Math.round((state.bank * 0.25) / 100) * 100);
  if (effect === 'next-x2') pendingMultiplier = Math.max(pendingMultiplier, 2) as BillionaireMultiplier;
  if (effect === 'next-x3') pendingMultiplier = 3;
  return {
    state: { ...state, bank: Math.max(0, state.bank + amountDelta), pendingMultiplier },
    amountDelta,
  };
}

export function formatTurkishLira(amount: number): string {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(amount);
}
